import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Alert, Button, Tag, message } from 'antd';
import Table from 'components/Common/Table';
import { BarChartOutlined, DownloadOutlined } from '@ant-design/icons';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import axios from 'axios';
import { Link } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';
import { formatMoney } from '../../utils/money';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (value) =>
  `₹${(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'this_fy' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  account: { type: 'string', default: 'all' },
  status: { type: 'string', default: 'PAID' },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const MonthlySalesReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const accountFilter = filters.account;
  const setAccountFilter = (value) => setFilters({ account: value });
  const statusFilter = filters.status;
  const setStatusFilter = (value) => setFilters({ status: value });
  const [accounts, setAccounts] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [invoiceData, setInvoiceData] = useState({});
  const [invoiceLoading, setInvoiceLoading] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
      const params = {
        filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
        start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };

      if (accountFilter !== 'all') params.customer_id = accountFilter;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await axios.get('/product/product/monthly-sales-report/', { params });
      if (response.status === 200) setData(response.data);
    } catch (err) {
      console.error('Error fetching monthly sales data:', err);
      setError(err.response?.data?.error || 'Failed to fetch monthly sales data');
    } finally {
      setLoading(false);
    }
  }, [filterPreset, dateRange, accountFilter, statusFilter]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get('/accounts/account/', { params: { page_size: 1000 } });
        if (response.status === 200) setAccounts(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (dateRange || filterPreset === 'all') fetchData();
  }, [fetchData, dateRange, filterPreset]);

  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
    });
  };

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]] });
    } else {
      setFilters({ preset: 'all', range: null });
    }
  };

  const handleClearFilters = () => {
    setFilters({ preset: 'this_fy', range: null, account: 'all', status: 'PAID' });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'this_fy') count++;
    if (accountFilter !== 'all') count++;
    if (statusFilter !== 'PAID') count++;
    return count;
  };

  const buildParams = useCallback(() => {
    const useDateRange = filterPreset === 'custom' || filterPreset === 'this_fy' || filterPreset === 'last_fy';
    const params = {
      filter_preset: useDateRange ? undefined : (filterPreset === 'all' ? 'all_time' : filterPreset),
      start_date: useDateRange && dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
      end_date: useDateRange && dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
    };
    if (accountFilter !== 'all') params.customer_id = accountFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    return params;
  }, [filterPreset, dateRange, accountFilter, statusFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { ...buildParams(), export: 'excel' };
      const response = await axios.get('/product/product/monthly-sales-report/', {
        params,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly_sales_report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      message.error('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const fetchInvoicesForMonth = async (monthKey) => {
    if (invoiceData[monthKey]) return; // Already cached
    setInvoiceLoading(prev => ({ ...prev, [monthKey]: true }));
    try {
      const params = { month: monthKey };
      if (accountFilter !== 'all') params.customer_id = accountFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get('/product/product/monthly-sales-invoices/', { params });
      if (response.status === 200) {
        setInvoiceData(prev => ({ ...prev, [monthKey]: response.data.invoices || [] }));
      }
    } catch (err) {
      console.error('Error fetching invoices for month:', err);
      message.error('Failed to load invoice details.');
    } finally {
      setInvoiceLoading(prev => ({ ...prev, [monthKey]: false }));
    }
  };

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys(prev => [...prev, record.month]);
      fetchInvoicesForMonth(record.month);
    } else {
      setExpandedRowKeys(prev => prev.filter(k => k !== record.month));
    }
  };

  // Clear invoice cache when filters change
  useEffect(() => {
    setInvoiceData({});
    setExpandedRowKeys([]);
  }, [filterPreset, dateRange, accountFilter, statusFilter]);

  const statusColors = {
    PAID: 'green', SENT: 'blue', PARTIAL: 'orange', DRAFT: 'default', CANCELLED: 'red',
  };

  const renderCurrencyAmount = (value, record, inrField) => {
    const isNonInr = record.currency_code && record.currency_code !== 'INR' && record.exchange_rate && record.exchange_rate != 1;
    const inrValue = record[inrField];
    return (
      <div>
        <span>{formatCurrency(isNonInr ? inrValue : value)}</span>
        {isNonInr && (
          <div style={{ fontSize: '11px', color: '#888' }}>
            {formatMoney(value, record.currency_code)}
          </div>
        )}
      </div>
    );
  };

  const invoiceColumns = [
    {
      title: 'Invoice #',
      dataIndex: 'inv_number',
      key: 'inv_number',
      width: 160,
      render: (text, record) => (
        <Link to={`/invoices/edit/${record.invoice_id}`} style={{ color: '#1890ff' }}>{text}</Link>
      ),
    },
    { title: 'Date', dataIndex: 'invoice_date', key: 'invoice_date', width: 110 },
    { title: 'Account', dataIndex: 'account_name', key: 'account_name', width: 200, ellipsis: true },
    {
      title: 'Currency',
      dataIndex: 'currency_code',
      key: 'currency_code',
      width: 80,
      render: (code) => code || 'INR',
    },
    {
      title: 'Revenue (Net)',
      dataIndex: 'net',
      key: 'net',
      align: 'right',
      width: 140,
      render: (value, record) => renderCurrencyAmount(value, record, 'inr_net'),
    },
    {
      title: 'Tax',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      align: 'right',
      width: 120,
      render: (value, record) => {
        const isNonInr = record.currency_code && record.currency_code !== 'INR' && record.exchange_rate && record.exchange_rate != 1;
        const inrTax = isNonInr ? (record.inr_total - record.inr_net) : value;
        return (
          <div>
            <span>{formatCurrency(isNonInr ? inrTax : value)}</span>
            {isNonInr && (
              <div style={{ fontSize: '11px', color: '#888' }}>
                {formatMoney(value, record.currency_code)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      align: 'right',
      width: 140,
      render: (value, record) => renderCurrencyAmount(value, record, 'inr_total'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <Tag color={statusColors[status] || 'default'}>{status}</Tag>,
    },
  ];

  const expandedRowRender = (record) => {
    const monthInvoices = invoiceData[record.month];
    const isLoading = invoiceLoading[record.month];

    if (isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="small" /> <Text style={{ marginLeft: 8 }}>Loading invoices...</Text>
        </div>
      );
    }

    if (!monthInvoices || monthInvoices.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '12px' }}>
          <Text type="secondary">No invoices found for this month.</Text>
        </div>
      );
    }

    return (
      <Table
        columns={invoiceColumns}
        dataSource={monthInvoices}
        rowKey="invoice_id"
        size="small"
        pagination={monthInvoices.length > 10 ? { defaultPageSize: 10, showSizeChanger: true, size: 'small' } : false}
        scroll={{ x: 'max-content' }}
        style={{ margin: '0 -8px' }}
      />
    );
  };

  const yAxisFormatter = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(0)}K`;
    return `₹${value}`;
  };

  const columns = [
    {
      title: 'Month',
      dataIndex: 'month_display',
      key: 'month_display',
      fixed: isMobile ? false : 'left',
      width: 120,
    },
    {
      title: 'Revenue (Net)',
      dataIndex: 'revenue',
      key: 'revenue',
      render: formatCurrency,
      sorter: (a, b) => a.revenue - b.revenue,
      align: 'right',
      width: 140,
    },
    {
      title: 'Tax',
      dataIndex: 'total_tax',
      key: 'total_tax',
      render: formatCurrency,
      sorter: (a, b) => a.total_tax - b.total_tax,
      align: 'right',
      width: 130,
    },
    {
      title: 'Revenue with Tax',
      dataIndex: 'revenue_with_tax',
      key: 'revenue_with_tax',
      render: formatCurrency,
      sorter: (a, b) => a.revenue_with_tax - b.revenue_with_tax,
      align: 'right',
      width: 160,
    },
    {
      title: 'Invoices',
      dataIndex: 'invoice_count',
      key: 'invoice_count',
      sorter: (a, b) => a.invoice_count - b.invoice_count,
      align: 'right',
      width: 100,
    },
  ];

  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Account</Text>
        <Select
          value={accountFilter}
          onChange={(value) => setAccountFilter(value || 'all')}
          style={{ width: '100%' }}
          placeholder="Select Account"
          showSearch
          allowClear
          optionFilterProp="children"
          filterOption={(input, option) => {
            const children = option?.children;
            return typeof children === 'string' ? children.toLowerCase().includes(input.toLowerCase()) : false;
          }}
        >
          <Option value="all">All Accounts</Option>
          {accounts.map(account => (
            <Option key={account.id} value={account.id}>{account.name}</Option>
          ))}
        </Select>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Status</Text>
        <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }} placeholder="Status">
          <Option value="all">All Statuses</Option>
          <Option value="PAID">Paid</Option>
          <Option value="SENT">Sent</Option>
          <Option value="PARTIAL">Partial</Option>
          <Option value="DRAFT">Draft</Option>
          <Option value="CANCELLED">Cancelled</Option>
        </Select>
      </div>
    </Space>
  );

  // Tables for the PDF export. Passed explicitly rather than scraped from the
  // rendered tables so the export carries every row, not just the page on screen.
  const pdfTables = [
    {
      title: 'Monthly Breakdown',
      columns: [
        { header: 'Month', key: 'month_display' },
        { header: 'Revenue (Net)', key: 'revenue', align: 'right', format: 'currency2' },
        { header: 'Tax', key: 'total_tax', align: 'right', format: 'currency2' },
        { header: 'Revenue with Tax', key: 'revenue_with_tax', align: 'right', format: 'currency2' },
        { header: 'Invoices', key: 'invoice_count', align: 'right' },
      ],
      rows: data?.monthly_data || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Revenue', value: `Rs. ${(data?.summary?.total_revenue || 0).toLocaleString('en-IN')}` },
    { label: 'Invoices', value: data?.summary?.total_invoices || 0 },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Monthly Sales Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Monthly Sales Report"
            subtitle={data?.summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="monthly-sales-report"
            disabled={loading || !data}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <div>
              <Space wrap size="middle">
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    size="small"
                    allowClear
                    placeholder={['Start Date', 'End Date']}
                  />
                </div>
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Account:</Text>
                  <Select
                    value={accountFilter}
                    onChange={(value) => setAccountFilter(value || 'all')}
                    size="small"
                    style={{ width: 200 }}
                    placeholder="Select Account"
                    showSearch
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const children = option?.children;
                      return typeof children === 'string' ? children.toLowerCase().includes(input.toLowerCase()) : false;
                    }}
                  >
                    <Option value="all">All Accounts</Option>
                    {accounts.map(account => (
                      <Option key={account.id} value={account.id}>{account.name}</Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Status:</Text>
                  <Select
                    value={statusFilter}
                    onChange={setStatusFilter}
                    size="small"
                    style={{ width: 150 }}
                    placeholder="Status"
                  >
                    <Option value="all">All Statuses</Option>
                    <Option value="PAID">Paid</Option>
                    <Option value="SENT">Sent</Option>
                    <Option value="PARTIAL">Partial</Option>
                    <Option value="DRAFT">Draft</Option>
                    <Option value="CANCELLED">Cancelled</Option>
                  </Select>
                </div>
              </Space>
            </div>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
        })}
        onClearFilters={handleClearFilters}
        title="Monthly Sales Report Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {/* Error Display */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          showIcon
          onClose={() => setError(null)}
          style={{ marginBottom: '16px' }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>
              <Text>Loading monthly sales data...</Text>
            </div>
          </div>
        </Card>
      )}

      {/* Summary + Chart + Table */}
      {!loading && data && (
        <>
          {/* Summary Statistics */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Revenue</span>}
                  value={data.summary?.total_revenue || 0}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#3f8600', fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Tax</span>}
                  value={data.summary?.total_tax || 0}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#cf1322', fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Revenue with Tax</span>}
                  value={data.summary?.total_revenue_with_tax || 0}
                  precision={2}
                  prefix="₹"
                  valueStyle={{ color: '#1890ff', fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Invoices</span>}
                  value={data.summary?.total_invoices || 0}
                  prefix={<BarChartOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Chart */}
          {data.monthly_data && data.monthly_data.length > 0 && (
            <Card
              size="small"
              style={{ marginBottom: '16px' }}
              title={<Text strong>Monthly Revenue Breakdown</Text>}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
                <ComposedChart
                  data={data.monthly_data}
                  margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="month_display"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tickFormatter={yAxisFormatter}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 55 : 65}
                  />
                  <Tooltip
                    formatter={(value, name) => [formatCurrency(value), name]}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue (Net)" fill="#52c41a" />
                  <Bar dataKey="total_tax" name="Tax" fill="#cf1322" />
                  <Line
                    type="monotone"
                    dataKey="revenue_with_tax"
                    name="Revenue with Tax"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={{ fill: '#1890ff', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Monthly Breakdown Table */}
          <Card
            size="small"
            style={{ marginBottom: '16px' }}
            title={
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div>
                  <Text strong>Monthly Breakdown</Text>
                  {!isMobile && (
                    <Text type="secondary" style={{ fontSize: '13px', marginLeft: '12px' }}>
                      {data.summary?.date_range || 'N/A'}
                    </Text>
                  )}
                </div>
                <Button
                  icon={<DownloadOutlined />}
                  size="small"
                  loading={exporting}
                  onClick={handleExport}
                >
                  {!isMobile && 'Export Excel'}
                </Button>
              </div>
            }
          >
            <Table
              columns={columns}
              dataSource={data.monthly_data || []}
              rowKey="month"
              size="small"
              expandable={{
                expandedRowRender,
                expandedRowKeys,
                onExpand: handleExpand,
              }}
              pagination={{
                defaultPageSize: 12,
                pageSizeOptions: ['6', '12', '24'],
                showSizeChanger: !isMobile,
                hideOnSinglePage: true,
                showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total}`,
                position: ['bottomRight'],
                simple: isMobile,
              }}
              scroll={{ x: 'max-content' }}
              summary={(pageData) => {
                if (!pageData || pageData.length === 0) return null;
                const totals = pageData.reduce(
                  (acc, row) => ({
                    revenue: acc.revenue + (row.revenue || 0),
                    total_tax: acc.total_tax + (row.total_tax || 0),
                    revenue_with_tax: acc.revenue_with_tax + (row.revenue_with_tax || 0),
                    invoice_count: acc.invoice_count + (row.invoice_count || 0),
                  }),
                  { revenue: 0, total_tax: 0, revenue_with_tax: 0, invoice_count: 0 }
                );
                return (
                  <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="right">{formatCurrency(totals.revenue)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">{formatCurrency(totals.total_tax)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">{formatCurrency(totals.revenue_with_tax)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">{totals.invoice_count}</Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          </Card>
        </>
      )}

      {/* No Data State */}
      {!loading && !data && !error && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No data available. Please select filters above.</Text>
          </div>
        </Card>
      )}

      {/* Empty Data State */}
      {!loading && data && data.monthly_data?.length === 0 && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Text type="secondary">No sales data found for the selected period.</Text>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MonthlySalesReport;
